import React from 'react';
import moment from 'moment';
import { CustomDatePicker } from '../../../components/DateTimePicker';
import { CustomTextField } from '../../../components/Textfield';
import { FileUploader } from '../../../components/UI';
import { showMessage } from '../../../utils/helper';
import {
    FieldRow,
    RejectReasonRow,
    formatDob,
    verifyKYCStyles,
} from './verifyKYCHeplers';
import { Box, Grid, Tooltip, Typography } from '@material-ui/core';
import { LinkOffOutlined, LinkOutlined } from '@material-ui/icons';
import DoneAllOutlinedIcon from '@material-ui/icons/DoneAllOutlined';
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import CancelPresentationOutlinedIcon from '@material-ui/icons/CancelPresentationOutlined';
import {
    CheckMarkGreen,
    CrossMarkIcon,
    ExclaimIcon,
} from '../../../assets/svg';
import FileUploaderTyped from '../../../components/UI/FileUploader/FileUploaderTyped';
import {
    KycImageRevealRow,
    useRevealedImage,
} from '../../../components/KYC/reveal';
import { isDigilockerVerified, isImageLink } from '../../../utils/appHelper';
import { PdfViewerButton } from '../../../components/Modals/PdfViewerModalV2';

interface PanStateCardProps {
    payload: any;
    setPayload: Function;
    panSkipped?: boolean;
}

const PanStateCard: React.FC<PanStateCardProps> = ({
    payload,
    setPayload,
    panSkipped,
}) => {
    // A live UNBLUR_IMAGE grant swaps in the raw object; otherwise this is whatever
    // the API served (the blurred variant, or nothing while masking is pending).
    const revealedImage = useRevealedImage(payload?.id, payload?.image);
    const styles = verifyKYCStyles({});
    const isDocumentIdCreated = !!payload?.id;
    const isManualVerificationRequired = payload?.manualVerificationRequired;
    const isApproved = payload?.approvalStatus === 'APPROVED';
    const isRejected = payload?.approvalStatus === 'REJECTED';
    const isPending = !isApproved && !isRejected;
    const isDocumentNumberMatching = !!payload?.isDocumentNumberMatching;
    const isAadharDobMatching = !!payload?.isDobMatching;
    const enableUpdate = !isApproved;
    let isVerifiedByDigiLocker = isDigilockerVerified(payload);
    const isPanSkipped = !!panSkipped;

    /*!isDocumentIdCreated ||
        (isManualVerificationRequired &&
            (!isDocumentNumberMatching || !isAadharDobMatching));*/
    console.log(
        'PanStateCard payload:',
        isManualVerificationRequired,
        enableUpdate,
        payload,
    );
    const isError =
        isManualVerificationRequired &&
        isPending &&
        (!isDocumentNumberMatching || !isAadharDobMatching);

    const statusText = isPanSkipped
        ? 'PAN Skipped'
        : !!isVerifiedByDigiLocker
          ? 'Verified by Digilocker'
          : !isDocumentIdCreated
            ? ''
            : isApproved
              ? 'Verified'
              : isRejected
                ? 'Rejected'
                : isManualVerificationRequired &&
                    (!isDocumentNumberMatching || !isAadharDobMatching)
                  ? 'Manual Verification Required'
                  : '';

    const statusColor = isPanSkipped
        ? '#ff9800'
        : !isDocumentIdCreated
          ? 'orange'
          : isApproved
            ? '#4caf50'
            : isRejected
              ? '#f44336'
              : isManualVerificationRequired &&
                  (!isDocumentNumberMatching || !isAadharDobMatching)
                ? '#f44336'
                : '';

    const renderTopIcons = () => (
        <Box display="flex" alignItems="baseline" ml="2px">
            {!!payload?.panVerified && (
                <>
                    <Tooltip
                        title={
                            !!payload?.verified ? 'Verified' : 'Not Verified'
                        }
                        placement="top"
                    >
                        {!!payload?.verified ? (
                            <DoneAllOutlinedIcon
                                className={styles.successIconStyle}
                            />
                        ) : (
                            <CancelPresentationOutlinedIcon
                                className={styles.errorIconStyle}
                            />
                        )}
                    </Tooltip>

                    {!!payload?.centralDobMismatch?.length && (
                        <Tooltip
                            title={`Verified DOB Mismatch - ${payload?.centralDobMismatch}`}
                            placement="top"
                        >
                            <AccountCircleOutlinedIcon
                                className={styles.errorIconStyle}
                            />
                        </Tooltip>
                    )}
                </>
            )}

            {!!payload?.ocrResponseCode &&
                ['435', '436'].includes(payload?.ocrResponseCode) && (
                    <Tooltip
                        title={
                            payload?.ocrResponseCode === '435'
                                ? 'Flagged - Not a Live Document'
                                : 'Flagged - Invalid Photo/Document'
                        }
                        placement="top"
                    >
                        <WarningRoundedIcon className={styles.errorIconStyle} />
                    </Tooltip>
                )}
        </Box>
    );

    const scannedId = payload?.autoKycDocumentNo;
    const enteredId = payload?.documentNo ?? '';
    const scannedDobRaw = payload?.autoKycDocumentDob;
    const enteredDobRaw = payload?.dob;
    const isOtherDocumentFound = !!payload?.pdfDocumentLink;

    return (
        <Grid
            item
            xs={6}
            className={
                isError
                    ? styles.gridItemContainerWithErrorBorder
                    : styles.gridItemContainer
            }
        >
            <Box
                width="100%"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
            >
                <Box display="flex" alignItems="center">
                    <Typography className={styles.title} variant="h6">
                        PAN Details
                    </Typography>
                    {!!isDocumentIdCreated && renderTopIcons()}
                </Box>

                <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography
                        style={{
                            fontWeight: 700,
                            marginLeft: 5,
                            color: statusColor,
                            fontSize: 14,
                        }}
                        variant="h6"
                        component="span"
                        gutterBottom
                    >
                        {statusText}
                    </Typography>
                    <Box ml="0.2rem">
                        {isApproved ? (
                            <CheckMarkGreen style={{ width: '16px' }} />
                        ) : isRejected ? (
                            <CrossMarkIcon style={{ width: '16px' }} />
                        ) : null}
                    </Box>
                </Box>
            </Box>

            {isRejected && !!payload?.rejectReason && (
                <Box style={{ width: '100%', marginTop: 8 }}>
                    <RejectReasonRow reason={payload?.rejectReason} />
                </Box>
            )}

            {/* Body: show different layout if manual verification with mismatch vs normal view */}
            {!isPanSkipped && (
                <Box style={{ width: '100%', marginTop: 8 }}>
                    {/* ID row */}
                    {!!isDocumentIdCreated && (
                        <FieldRow
                            label={scannedId ? 'Scanned Id:' : 'Entered Id:'}
                            scannedValue={scannedId}
                            enteredValue={enteredId}
                            showEdited={
                                !!scannedId &&
                                !!enteredId &&
                                !!payload?.docNumberMismatch
                            }
                            showWarning={!isDocumentNumberMatching}
                            documentId={payload?.id}
                            documentType={'PANCARD'}
                            revealable={payload?.revealable}
                            revealSubject={'PAN number'}
                        />
                    )}

                    {/* DOB row */}
                    {!!isDocumentIdCreated && (
                        <FieldRow
                            label={
                                scannedDobRaw ? 'Scanned DOB:' : 'Entered DOB:'
                            }
                            scannedValue={
                                scannedDobRaw
                                    ? formatDob(scannedDobRaw)
                                    : undefined
                            }
                            enteredValue={
                                enteredDobRaw
                                    ? formatDob(enteredDobRaw)
                                    : undefined
                            }
                            showEdited={
                                !!scannedDobRaw &&
                                !!enteredDobRaw &&
                                formatDob(scannedDobRaw) !==
                                    formatDob(enteredDobRaw)
                            }
                            showWarning={!isAadharDobMatching}
                        />
                    )}
                    {!!isOtherDocumentFound && (
                        <PdfViewerButton
                            file={payload?.pdfDocumentLink}
                            variant="link"
                            linkText="Click to view PDF document from DigiLocker"
                        />
                    )}
                    {/* If manual verification with mismatch show editable read-only field as before */}
                    {enableUpdate ? (
                        <Box width={'100%'}>
                            <CustomTextField
                                value={enteredId}
                                onChange={(e: any) =>
                                    setPayload({
                                        ...payload,
                                        documentNo: e?.target?.value?.trim(),
                                    })
                                }
                                label="PAN Number"
                                variant="outlined"
                                style={{
                                    width: '100%',
                                    marginBottom: 16,
                                    marginTop: 8,
                                }}
                                InputProps={{
                                    readOnly: !enableUpdate,
                                    //endAdornment: <ExclaimIcon />,
                                }}
                            />
                        </Box>
                    ) : null}
                </Box>
            )}

            {!isPanSkipped && !isVerifiedByDigiLocker && (
                <KycImageRevealRow
                    documentId={payload?.id}
                    documentType={'PANCARD'}
                    imageRevealable={payload?.imageRevealable}
                    imageUnmasked={payload?.imageUnmasked}
                    subject={'PAN image'}
                />
            )}

            {!isPanSkipped && !isVerifiedByDigiLocker && (
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    width={'100%'}
                >
                    <Box width={'100%'}>
                        <FileUploaderTyped
                            labelStyles={{
                                fontSize: '14px',
                                fontWeight: 600,
                            }}
                            disabled={!enableUpdate}
                            defaultValue={
                                !!isImageLink(revealedImage)
                                    ? revealedImage
                                    : null
                            }
                            flowType="KYC"
                            placeholder={'Upload PAN (Front)'}
                            onFileUploaded={(e: any) => {
                                setPayload({
                                    ...payload,
                                    image: e,
                                });
                            }}
                            onRemoved={(e: any) => {
                                setPayload({
                                    ...payload,
                                    image: null,
                                });
                            }}
                        />
                    </Box>
                </Box>
            )}
        </Grid>
    );
};

export default PanStateCard;
